/**
 * La ficha de un cobro.
 *
 * Un diálogo y no una página propia porque un cobro no se edita: se mira una
 * vez, se comprueba un dato y se cierra. Darle dirección propia obligaría a
 * navegar de ida y vuelta para consultar tres.
 *
 * Lo que se enseña viene de dos sitios y la ficha lo distingue: arriba lo que
 * guardamos —qué se vendió, a quién, cuándo—, que no cambia nunca, y debajo lo
 * que se le pregunta a Stripe al abrir —tarjeta, comisión, reembolsos—, que sí.
 * Si Stripe no contesta, esa parte se cae sola y el resto sigue en pie.
 *
 * Reembolsar sí se puede, y no lo hemos escrito nosotros. El dinero y la
 * responsabilidad son del vendedor —son cargos directos en su cuenta—, así que
 * un botón nuestro nos haría dueños de un flujo que no es nuestro: parciales,
 * disputas, plazos. «Ver el cobro en Stripe» abre encima el componente
 * incrustado de Connect, que es esa misma ventana servida por Stripe para su
 * cuenta. Nosotros ponemos lo que Stripe no sabe: qué oferta, qué comprador y
 * qué otras mensualidades lleva.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { formatDashboardListDateTime } from '../../../lib/dashboardListDate'
import { orderCountTitle, orderProductCount, orderStatusLabel, orderStatusTone } from './offerShared'
import { disputeNeedsAction, disputeUnderReview, getOrder, type Order, type OrderDetail, type OrderItem } from '../../../lib/api'
import type { Translate } from '@webbuilder/i18n'
import { useI18n } from '../../../lib/i18n'
import { StripePaymentDetails, isEmbeddedStripeConfigured } from './StripePaymentDetails'
import { StripeDisputeDetails } from './StripeDisputeDetails'
import { OrderShippingPanel } from './OrderShippingPanel'
import { IconAlertTriangle, IconArrowBackUp, IconArrowUpRight, IconCalendarEvent, IconCircleCheck, IconClock, IconCreditCard, IconExternalLink, IconInfoCircle, IconLoader2, IconMail, IconPackage, IconReceipt, IconStack2, IconTruckDelivery, IconUser, IconX } from '@tabler/icons-react'
import { moneyLocale } from '../../../lib/region'

/** Un importe en la unidad mínima, como lo formatea el resto del panel. */
function money(amount: number, currency: string): string {
  const upper = (currency || 'usd').toUpperCase()
  try {
    /* Con el locale de la REGIÓN del proyecto, no con uno fijo: escrito a mano
       decía «MX$» en todos los proyectos, fuera cual fuera su país. El importe
       es de un negocio concreto, así que se escribe como allí. Ver `region.tsx`. */
    return new Intl.NumberFormat(moneyLocale(), { style: 'currency', currency: upper }).format(amount / 100)
  } catch {
    return `${(amount / 100).toFixed(2)} ${upper}`
  }
}

/**
 * Cuánto queda de plazo, en la frase que va dentro del aviso de la ficha.
 *
 * Días y no la fecha, por lo mismo que en la banda de arriba: lo que decide si
 * esto se atiende ahora es cuánto queda, y una fecha obliga a restar. Devuelve
 * cadena vacía cuando no hay plazo — hay disputas que llegan sin él— para que la
 * frase se quede sin esa parte en vez de decir «quedan NaN días».
 */
function disputeDueLabel(dueBy: string | null | undefined, t: Translate): string {
  if (!dueBy) return ''
  const when = new Date(dueBy).getTime()
  if (!Number.isFinite(when)) return ''
  const startOfDay = (value: number) => {
    const date = new Date(value)
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  }
  const days = Math.round((startOfDay(when) - startOfDay(Date.now())) / 86_400_000)
  if (days < 0) return t('orders.detail.due.past')
  if (days === 0) return t('orders.detail.due.today')
  if (days === 1) return t('orders.detail.due.one')
  return t('orders.detail.due.many', { days })
}

/**
 * Un dato del pedido: su rótulo con icono arriba y el valor debajo.
 *
 * El icono es el mismo gesto que en la ficha de un contacto: al recorrer la
 * columna se reconoce de qué habla cada caja antes de leer el rótulo. Opcional
 * porque las listas de la suscripción y del cobro en Stripe son enumeraciones
 * —cobro 1, cobro 2, comisión, neto— y ahí un dibujo por renglón sería ruido.
 */
function Row({ label, icon: Icon, children }: { label: string; icon?: typeof IconReceipt; children: React.ReactNode }) {
  return (
    <div className="dash-order-detail-row">
      <dt>{Icon ? <Icon size={13} aria-hidden="true" /> : null} {label}</dt>
      <dd>{children}</dd>
    </div>
  )
}

/**
 * El pago concreto en el panel de Stripe del vendedor.
 *
 * Con la cuenta en la ruta: quien mira esto tiene varias —la suya y las de sus
 * clientes si es una agencia— y sin ella Stripe abre la que tuviera puesta, que
 * puede no ser la del cobro. Sin identificador del pago se cae a la lista de la
 * cuenta, que sigue siendo mejor que la lista de una cuenta cualquiera.
 */
function stripePaymentUrl(accountId: string, paymentIntentId: string | null): string {
  const base = accountId ? `https://dashboard.stripe.com/${accountId}` : 'https://dashboard.stripe.com'
  return paymentIntentId ? `${base}/payments/${paymentIntentId}` : `${base}/payments`
}

export function OrderDetailDialog({ projectId, token, order, onOpenOffer, onOpenContact, onClose }: {
  projectId: string
  token: string
  /** La fila que se pulsó: pinta la ficha entera antes de que llegue el resto. */
  order: Order
  /** Abre la oferta que se vendió, para editarla. */
  onOpenOffer?: ((offerId: string) => void) | undefined
  /**
   * Abre la ficha de quien compró.
   *
   * La identidad es la misma que usa Contactos —`email:` y la dirección en
   * minúsculas—, que es como el servidor agrupa a quien escribe y a quien paga.
   * Opcional: abierto DESDE un contacto no se pasa, porque llevaría a la ficha
   * que ya está debajo.
   */
  onOpenContact?: ((identity: string) => void) | undefined
  onClose: () => void
}) {
  /* Cómo llama Contactos a esta persona: su dirección en minúsculas, que es
     como el servidor agrupa a quien escribe y a quien compra. Vacío sin correo:
     entonces no hay ficha que abrir. */
  const buyerEmail = (order.buyerEmail ?? '').trim().toLowerCase()
  const buyerIdentity = buyerEmail ? `email:${buyerEmail}` : ''
  const [detail, setDetail] = useState<OrderDetail | null>(null)
  const { t, fmt } = useI18n()
  /* Un pedido físico abre siempre en «Envío»: es lo que se viene a hacer.
     Lo dice la fila de la lista, que llega antes que el detalle. */
  const hasShipping = Boolean(order.fulfillmentStatus ?? detail?.shipping)
  const [tab, setTab] = useState<'shipping' | 'purchase'>('shipping')
  /** La ventana de Stripe, encima de ésta. Se pide sólo cuando se abre. */
  const [embedOpen, setEmbedOpen] = useState(false)
  /** La ventana de la disputa, aparte de la del cobro: son dos pantallas. */
  const [disputeModalOpen, setDisputeModalOpen] = useState(false)
  /* Si la ficha no llega. La pestaña «Compra» se arregla con lo que traía la
     fila, pero «Envío» sólo existe con el detalle: sin esto se quedaba en
     «Cargando» para siempre, sin decir que había fallado. */
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)

  useEffect(() => {
    let alive = true
    setDetail(null)
    setLoadFailed(false)
    getOrder(projectId, order.id, token)
      .then((row) => { if (alive) setDetail(row) })
      /* Keep the saved summary usable if the live payment detail fails. */
      .catch((failure: unknown) => {
        console.error('[ventas] no se pudo abrir el cobro', failure)
        if (alive) setLoadFailed(true)
      })
    return () => { alive = false }
  }, [projectId, order.id, token, loadAttempt])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const stripe = detail?.stripe ?? null
  /* Mientras el detalle viaja se enseña lo que traía la fila de la lista; en
     cuanto llega manda él, que es el que acaba de hablar con Stripe. */
  const refundedAmount = detail?.amountRefunded ?? order.amountRefunded
  const status = detail?.status ?? order.status
  const subscription = detail?.subscription ?? null
  /* Lo que se llevó, línea a línea. Del detalle si ya llegó —trae la foto de
     cada producto, la fila de la lista no—; un pedido de una sola oferta se
     escribe como una línea más para que la tarjeta sea siempre la misma. */
  const lines: OrderItem[] = detail?.items?.length ? detail.items
    : order.items?.length ? order.items
      : [{
        offerId: order.offerId,
        title: order.offerTitle,
        variantLabel: order.variantLabel ?? null,
        quantity: order.quantity ?? 1,
        kind: 'physical',
        image: order.offerImage ?? null,
      }]
  /* Lo cobrado por esta suscripción hasta hoy. Se suma aquí y no en el servidor
     porque los importes ya vienen: pedir una cifra más sería otra consulta para
     lo mismo. Todos comparten moneda —es la del precio contratado—. */
  const subscriptionTotal = (subscription?.payments ?? []).reduce((total, payment) => total + payment.amountTotal, 0)
  const stripeUrl = stripePaymentUrl(detail?.stripeAccountId ?? '', stripe?.paymentIntentId ?? null)
  /*
   * Un pedido que nunca pasó por la pasarela.
   *
   * Es lo que deja una oferta gratuita: se reclama con un formulario, no se
   * cobra, y por eso su `stripeAccountId` va nulo — ver la migración que lo
   * permitió. Sin esto la ficha abría un apartado «Pago en Stripe» para decir
   * que Stripe no devolvió el detalle, y remataba con un botón que lleva al
   * panel de Stripe a buscar un cobro que no existe. Las dos cosas dan a
   * entender que algo falló, cuando lo que pasa es que aquí no había nada que
   * cobrar.
   */
  const throughStripe = detail ? detail.stripeAccountId !== null : order.amountTotal > 0
  /* Para incrustarlo hacen falta las dos cosas: la clave pública del panel y un
     cobro que la cuenta conectada reconozca. Sin alguna, el enlace de siempre. */
  const embeddable = Boolean(stripe?.paymentIntentId) && isEmbeddedStripeConfigured()

  /* La disputa, del detalle si ya llegó y de la fila mientras tanto — igual que
     el estado y lo devuelto. Sólo las que todavía se pueden pelear: una perdida
     o ganada es historia, y un bloque rojo con un botón de responder encima de
     algo cerrado manda a nadie a una pantalla donde no hay nada que hacer. */
  const disputeStatus = detail?.disputeStatus ?? order.disputeStatus
  const hasDispute = disputeNeedsAction(disputeStatus)
  /* Ya respondida, sin veredicto. Aquí sí se enseña —al revés que en el aviso de
     arriba, que es una cola de pendientes—: quien abre la ficha viene a saber en
     qué quedó aquello, y la ausencia de bloque diría que en nada. */
  const underReview = !hasDispute && disputeUnderReview(disputeStatus)
  const disputeAmount = detail?.disputeAmount ?? order.disputeAmount
  const disputeDue = disputeDueLabel(detail?.disputeDueBy ?? order.disputeDueBy, t)
  const disputeEmbeddable = embeddable && hasDispute
  /* La misma ventana de Stripe, abierta para mirar: enseña las pruebas que se
     mandaron y en qué punto va. Es lo único que se puede ofrecer aquí, y sin
     ella el bloque afirmaría «ya se respondió» sin manera de comprobarlo. */
  const reviewEmbeddable = embeddable && underReview

  return createPortal(
    <div
      className="dash-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <section className="dash-order-detail" role="dialog" aria-modal="true" aria-label={t('orders.detail.dialogAria')}>
        <header className="dash-offer-wizard-head">
          {/* La portada de lo que se vendió, cuando la oferta sigue existiendo.
              Reconocer la compra es lo primero que se hace al abrir la ficha, y
              una imagen lo resuelve antes que un título. */}
          {order.offerImage ? (
            <span className="dash-order-detail-thumb"><img src={order.offerImage} alt="" /></span>
          ) : (
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconReceipt size={16} /></span>
          )}
          <div className="dash-offer-wizard-head-copy">
            <h2>
              {/* El número del pedido delante: es lo que cita el cliente. */}
              {order.number ? <span className="dash-order-number is-title">#{order.number}</span> : null}
              {orderProductCount(order) ? orderCountTitle(orderProductCount(order)!, t) : (order.offerTitle || t('orders.detail.offerDeleted'))}
            </h2>
            <p>
              {money(order.amountTotal, order.currency)} · {formatDashboardListDateTime(order.createdAt)}
              {/* Editar lo que se vendió, desde donde se ve vendido. Sólo si la
                  oferta sigue viva: el cobro conserva su título aunque se
                  borre, y un enlace a lo que ya no existe es una promesa rota. */}
              {order.offerId && onOpenOffer ? (
                <>
                  {' · '}
                  <button type="button" className="dash-order-detail-offer-link" onClick={() => onOpenOffer(order.offerId as string)}>
                    {t('orders.detail.viewOffer')} <IconArrowUpRight size={12} />
                  </button>
                </>
              ) : null}
            </p>
          </div>
          <button type="button" className="dash-create-close" aria-label={t('orders.detail.close')} onClick={onClose}>
            <IconX size={18} />
          </button>
        </header>

        <div className="dash-order-detail-body">
          {/* La disputa va antes que nada y fuera de la lista de datos.
              Dentro sería una fila más entre el comprador y la fecha, del mismo
              tamaño que el correo, y esto no es un dato del cobro: es lo único
              de esta pantalla que tiene fecha de caducidad. Quien llega aquí
              desde el aviso de arriba viene a esto y a nada más. */}
          {hasDispute ? (
            <div className="dash-order-dispute" role="alert">
              <span className="dash-order-dispute-icon" aria-hidden="true">
                <IconAlertTriangle size={16} />
              </span>
              <div className="dash-order-dispute-copy">
                <strong>{t('orders.detail.disputeTitle')}</strong>
                <p>
                  {t('orders.detail.disputeBody1', { amount: money(disputeAmount, order.currency) })}
                  {disputeDue ? ` ${disputeDue}` : ''} {t('orders.detail.disputeBody2')}
                </p>
              </div>
              {/* La acción va aquí y también abajo: aquí porque es donde se lee
                  el problema, y abajo porque es donde están las acciones de la
                  ficha. Es el mismo botón, no dos caminos distintos.

                  Y si no se puede incrustar, el enlace a Stripe en su lugar.
                  Antes en ese caso el bloque se quedaba SIN acción: un recuadro
                  rojo que avisa de que el dinero se pierde si nadie responde y
                  no ofrece por dónde responder. La ventana incrustada pide la
                  clave pública del panel y un cobro que la cuenta conectada
                  reconozca; cuando falta alguna de las dos, responder sigue
                  siendo posible —en el panel de Stripe— y es lo que hay que
                  decir. */}
              {disputeEmbeddable ? (
                <button type="button" className="dash-order-dispute-cta" onClick={() => setDisputeModalOpen(true)}>
                  {t('orders.detail.respond')} <IconArrowUpRight size={13} />
                </button>
              ) : throughStripe ? (
                <a
                  className="dash-order-dispute-cta"
                  href={stripeUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('orders.detail.respond')} <IconArrowUpRight size={13} />
                </a>
              ) : null}
            </div>
          ) : underReview ? (
            /* El mismo bloque en gris y sin `role="alert"`: no interrumpe a
               nadie, informa a quien ya está mirando. Lo que hace falta saber es
               que la respuesta se entregó, que el dinero sigue retenido y que
               quien decide no somos ni nosotros ni Stripe. */
            <div className="dash-order-dispute is-review">
              <span className="dash-order-dispute-icon" aria-hidden="true">
                <IconClock size={16} />
              </span>
              <div className="dash-order-dispute-copy">
                <strong>{t('orders.detail.reviewTitle')}</strong>
                <p>
                  {t('orders.detail.reviewBody', { amount: money(disputeAmount, order.currency) })}
                  {' '}{t('orders.detail.reviewBody2')}
                </p>
              </div>
              {reviewEmbeddable ? (
                <button type="button" className="dash-order-dispute-cta is-review" onClick={() => setDisputeModalOpen(true)}>
                  {t('orders.detail.view')} <IconArrowUpRight size={13} />
                </button>
              ) : null}
            </div>
          ) : null}

          {/* Un producto físico: dos pestañas, «Envío» —lo que hay que hacer— y
              «Compra» —los datos del cobro—. Juntas en una lista eran dos cosas
              distintas mezcladas, y no se veía qué tocaba hacer. */}
          {hasShipping ? (
            <div className={`dash-svc-segmented dash-order-tabs${tab === 'purchase' ? ' is-second' : ''}`} role="tablist">
              {/* Cada pestaña con su miniatura, como «Tu marca | Estilos» del
                  editor: el paquete en camino y el recibo. Apagada la de fuera,
                  con color al elegirla o al pasar por encima. */}
              <button type="button" role="tab" aria-selected={tab === 'shipping'} className={tab === 'shipping' ? 'is-on' : ''} onClick={() => setTab('shipping')}>
                <span className="dash-order-tab-art is-shipping" aria-hidden="true">
                  <IconTruckDelivery size={17} stroke={1.8} />
                  <i />
                </span>
                <span className="dash-order-tab-label">{t('orders.detail.tabShipping')}</span>
              </button>
              <button type="button" role="tab" aria-selected={tab === 'purchase'} className={tab === 'purchase' ? 'is-on' : ''} onClick={() => setTab('purchase')}>
                <span className="dash-order-tab-art is-purchase" aria-hidden="true">
                  <span className="dash-order-tab-sheet"><i /><i /><i /></span>
                  <b />
                </span>
                <span className="dash-order-tab-label">{t('orders.detail.tabPurchase')}</span>
              </button>
            </div>
          ) : null}

          {hasShipping && tab === 'shipping' ? (
            detail?.shipping ? (
              <OrderShippingPanel
                projectId={projectId}
                token={token}
                orderId={order.id}
                shipping={detail.shipping}
                events={detail.events ?? []}
                currency={order.currency}
                onChange={(shipping) => setDetail((current) => (current ? { ...current, shipping } : current))}
                /* Tras un cambio se vuelve a pedir la ficha: el historial
                   tiene una línea nueva (y quizá el correo enviado). */
                onRefresh={() => { void getOrder(projectId, order.id, token).then(setDetail).catch(() => {}) }}
              />
            ) : loadFailed ? (
              <p className="dash-order-tab-loading is-error" role="alert">
                <IconAlertTriangle size={14} /> {t('orders.detail.loadFailed')}
                <button type="button" className="dash-order-tab-retry" onClick={() => setLoadAttempt((n) => n + 1)}>{t('common.tryAgain')}</button>
              </p>
            ) : <p className="dash-order-tab-loading"><IconLoader2 size={14} className="dash-spin" /> {t('orders.loading')}</p>
          ) : (
            <>
          {/* «Compra» en dos tarjetas y no una caja por dato: arriba lo que se
              llevó, como un recibo —cada línea con su foto y, al pie, envío,
              cupón, impuesto y total—; debajo quién, en qué estado y cuándo.
              Con una caja por fila eran nueve rectángulos iguales y la lista de
              productos quedaba apretada a la derecha de la suya. La oferta ya
              no tiene fila: es el titular de arriba, con su enlace. */}
          <section className="dash-order-card">
            <span className="dash-order-card-kicker">
              <IconStack2 size={13} aria-hidden="true" />
              {lines.length > 1 ? t('orders.detail.items', { count: lines.length }) : t('orders.detail.item')}
            </span>
            <ul className="dash-order-lines">
              {lines.map((item, index) => (
                <li key={`${item.offerId ?? 'x'}-${index}`}>
                  <span className="dash-order-line-img" aria-hidden="true">
                    {item.image ? <img src={item.image} alt="" loading="lazy" /> : <IconPackage size={16} />}
                  </span>
                  <span className="dash-order-line-copy">
                    {item.offerId && onOpenOffer
                      ? <button type="button" className="dash-order-detail-buyer-link" onClick={() => onOpenOffer(item.offerId as string)}>{item.title || t('orders.detail.offerDeleted')}</button>
                      : <strong>{item.title || t('orders.detail.offerDeleted')}</strong>}
                    {item.variantLabel ? <small>{item.variantLabel}</small> : null}
                  </span>
                  <span className="dash-order-line-qty">{item.quantity > 1 ? `× ${item.quantity}` : ''}</span>
                  <span className="dash-order-line-total">{item.totalFormatted ?? ''}</span>
                </li>
              ))}
            </ul>
            <dl className="dash-order-totals">
              {detail?.shipping?.rateName ? (
                <div>
                  <dt>{t('orders.detail.tabShipping')} · {detail.shipping.rateName}</dt>
                  <dd>{detail.shipping.amountFormatted}</dd>
                </div>
              ) : null}
              {/* El cupón, si entró con uno: es lo primero que se pregunta cuando
                  un importe no coincide con el precio de la oferta. */}
              {detail?.couponCode ? (
                <div>
                  <dt>{t('orders.detail.coupon')} <code className="dash-order-coupon-code">{detail.couponCode}</code></dt>
                  <dd>{detail.discountFormatted ? `− ${detail.discountFormatted}` : ''}</dd>
                </div>
              ) : null}
              {/* Sólo cuando lo hubo: con Stripe Tax apagado —lo normal— una fila
                  de «Impuestos: 0» haría pensar que algo no se cobró. */}
              {detail?.taxFormatted ? (
                <div>
                  <dt>{t('orders.detail.tax')}</dt>
                  <dd>{detail.taxFormatted}</dd>
                </div>
              ) : null}
              <div className="is-total">
                <dt>{t('orders.detail.total')}</dt>
                {/* Con la moneda escrita: «$0.00» es dólar, peso o varias cosas
                    más según el país. */}
                <dd>
                  {money(order.amountTotal, order.currency)}
                  {' '}
                  <span className="dash-order-detail-currency">{(order.currency || 'usd').toUpperCase()}</span>
                </dd>
              </div>
            </dl>
          </section>

          <section className="dash-order-card">
            <dl className="dash-order-facts">
              <div>
                <dt><IconUser size={14} aria-hidden="true" /> {t('orders.detail.customer')}</dt>
                <dd>
                  {/* A su ficha: quien mira un cobro suele querer saber quién es
                      esta persona y qué más ha hecho. Sin correo no hay a quién
                      abrir —la ficha se agrupa por dirección—. */}
                  {onOpenContact && buyerIdentity ? (
                    <button type="button" className="dash-order-detail-buyer-link" onClick={() => onOpenContact(buyerIdentity)}>
                      {order.buyerName || t('orders.noName')}
                    </button>
                  ) : (<strong>{order.buyerName || t('orders.noName')}</strong>)}
                  {/* Si la tarjeta venía a otro nombre (un regalo, la de la pareja). */}
                  {order.cardName && order.cardName.trim() && order.cardName.trim() !== (order.buyerName ?? '').trim()
                    ? <span className="dash-order-facts-sub">{t('orders.detail.cardName', { name: order.cardName.trim() })}</span>
                    : null}
                  {order.buyerEmail
                    ? <a className="dash-order-facts-sub" href={`mailto:${order.buyerEmail}`}><IconMail size={12} aria-hidden="true" /> {order.buyerEmail}</a>
                    : <em className="dash-order-facts-sub">{t('orders.noEmail')}</em>}
                </dd>
              </div>
              <div>
                <dt><IconCircleCheck size={14} aria-hidden="true" /> {t('orders.detail.status')}</dt>
                <dd>
                  {/* Sin pasarela, «Gratis» y no «Pagado»: nadie pagó nada. */}
                  <span className={`dash-offer-status ${throughStripe ? orderStatusTone(status) : 'is-draft'}`}>
                    {throughStripe ? orderStatusLabel(status, t) : t('orders.status.free')}
                  </span>
                </dd>
              </div>
              {/* La fecha entera con su hora: en la ficha la hora es un dato, es
                  cuándo entró el dinero. */}
              <div>
                <dt><IconCalendarEvent size={14} aria-hidden="true" /> {t('orders.detail.date')}</dt>
                <dd>{fmt.dateTime(order.createdAt)}</dd>
              </div>
            </dl>
          </section>

          {throughStripe ? (
            <section className="dash-order-card" aria-label={t('orders.detail.paymentAndRefunds')}>
              <span className="dash-order-card-kicker"><IconCreditCard size={13} aria-hidden="true" />{t('orders.detail.paymentAndRefunds')}</span>
              <dl className="dash-order-facts">
                {stripe?.cardLast4 ? (
                  <div>
                    <dt><IconCreditCard size={14} aria-hidden="true" />{t('orders.detail.paymentMethod')}</dt>
                    <dd>{stripe.cardBrand ? `${stripe.cardBrand.toUpperCase()} · ` : ''}•••• {stripe.cardLast4}</dd>
                  </div>
                ) : null}
                <div>
                  <dt><IconArrowBackUp size={14} aria-hidden="true" />{t('orders.detail.refunds')}</dt>
                  <dd>{refundedAmount > 0 ? <strong>{money(refundedAmount, order.currency)}</strong> : t('orders.detail.noRefunds')}</dd>
                </div>
                {refundedAmount > 0 ? (
                  <div>
                    <dt>{t('orders.detail.unrefunded')}</dt>
                    <dd>{money(Math.max(0, order.amountTotal - refundedAmount), order.currency)}</dd>
                  </div>
                ) : null}
              </dl>
              {!detail && !loadFailed ? (
                <p className="dash-order-payment-note" role="status"><IconLoader2 size={13} aria-hidden="true" />{t('orders.detail.updatingPayment')}</p>
              ) : !stripe ? (
                <p className="dash-order-payment-note">{t('orders.detail.savedPayment')}{' '}
                  <button type="button" className="dash-order-detail-buyer-link" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>{t('orders.detail.retryPayment')}</button>
                </p>
              ) : null}
              {stripe?.paymentIntentId ? (
                embeddable ? (
                  <button type="button" className="dash-order-detail-buyer-link dash-order-refund-link" onClick={() => setEmbedOpen(true)}>
                    <IconArrowBackUp size={14} aria-hidden="true" />{t('orders.detail.manageRefunds')}
                  </button>
                ) : (
                  <a className="dash-order-detail-buyer-link dash-order-refund-link" href={stripeUrl} target="_blank" rel="noreferrer">
                    <IconArrowUpRight size={14} aria-hidden="true" />{t('orders.detail.manageRefunds')}
                  </a>
                )
              ) : null}
            </section>
          ) : null}


          {/* Una suscripción no se entiende mirando un cobro suelto: la pregunta
              que trae es cuántas veces ha pagado esta persona y desde cuándo. Se
              responde aquí, con los cobros delante, en vez de mandar a buscarlos
              filtrando la lista. */}
          {subscription ? (
            <div className="dash-order-detail-section">
              <span className="dash-order-detail-section-title">{t('orders.detail.subscription')}</span>
              <dl className="dash-order-detail-list">
                <Row label={t('orders.detail.thisCharge')}>
                  {subscription.number > 0
                    ? t('orders.detail.chargeOf', { number: subscription.number, total: subscription.payments.length })
                    : t('orders.detail.fromSubscription')}
                </Row>
                <Row label={t('orders.detail.customerSince')}>
                  {subscription.payments[0]
                    ? formatDashboardListDateTime(subscription.payments[0].createdAt)
                    : <em>—</em>}
                </Row>
                <Row label={t('orders.detail.chargedTotal')}>
                  <strong>{money(subscriptionTotal, order.currency)}</strong>
                </Row>
              </dl>
              <ul className="dash-order-detail-payments">
                {subscription.payments.map((payment, index) => (
                  <li key={payment.id} className={payment.id === order.id ? 'is-current' : ''}>
                    <span className="dash-order-detail-payment-num">{index + 1}</span>
                    <time dateTime={payment.createdAt}>{formatDashboardListDateTime(payment.createdAt)}</time>
                    <span className="dash-order-detail-payment-amount">{payment.formatted}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

            </>
          )}

          {/* Stripe keeps the full transaction timeline and refund workflow. */}
        </div>

        <footer className="dash-order-detail-foot">
          {/* Delante de recibo y cobro, y en primario: de las tres acciones de
              esta ficha es la única con fecha. Las otras dos siguen ahí para
              cuando no la haya. */}
          {disputeEmbeddable ? (
            <button type="button" className="dash-primary-btn dash-order-dispute-foot-btn" onClick={() => setDisputeModalOpen(true)}>
              <IconAlertTriangle size={14} /> {t('orders.detail.disputeCta')}
            </button>
          ) : reviewEmbeddable ? (
            /* Secundario y detrás del recibo no: en revisión ya no es la acción
               con fecha, pero sigue siendo lo que se viene a mirar en una venta
               disputada — así que conserva el sitio y pierde el rojo. */
            <button type="button" className="dash-secondary-btn" onClick={() => setDisputeModalOpen(true)}>
              <IconClock size={14} /> {t('orders.detail.reviewCta')}
            </button>
          ) : null}
          {stripe?.receiptUrl ? (
            <a className="dash-secondary-btn" href={stripe.receiptUrl} target="_blank" rel="noreferrer">
              <IconReceipt size={14} /> {t('orders.detail.receipt')}
            </a>
          ) : null}
          {/* Reembolsar y disputar son decisiones del vendedor, y siguen siendo
              de Stripe: lo que cambia es que ya no hay que salir del panel para
              tomarlas. Se abre encima la misma ventana que él vería allí, con su
              cronología y sus botones, servida por Stripe para su cuenta.

              Si falta la clave pública o el cobro no tiene identificador —una
              venta de prueba, un cobro que ya no existe— se cae al enlace de
              siempre, que al menos lleva a la puerta correcta. */}
          {/* Nada de Stripe cuando no se pasó por Stripe: el enlace llevaría a
              buscar en su panel un cobro que nunca existió. */}
          {/* Principal, salvo que ya lo sea la disputa: desde que el detalle de
              Stripe salió de la ficha, esto es por donde se llega a él, así que
              tiene que verse como la salida y no como una opción más al lado
              del recibo. Con una disputa por responder se queda en secundario
              —ésa es la acción con fecha— para que no haya dos principales
              compitiendo en el mismo pie. */}
          {!throughStripe ? null : embeddable ? (
            <button
              type="button"
              className={disputeEmbeddable ? 'dash-secondary-btn' : 'dash-primary-btn'}
              onClick={() => setEmbedOpen(true)}
            >
              <IconInfoCircle size={14} /> {t('orders.detail.openInStripe')}
            </button>
          ) : (
            <a
              className={disputeEmbeddable ? 'dash-secondary-btn' : 'dash-primary-btn'}
              href={stripeUrl}
              target="_blank"
              rel="noreferrer"
            >
              <IconExternalLink size={14} /> {t(stripe?.paymentIntentId ? 'orders.detail.manageInStripe' : 'orders.detail.openStripe')}
            </a>
          )}
        </footer>
      </section>
      {embedOpen && stripe?.paymentIntentId ? (
        <StripePaymentDetails
          projectId={projectId}
          token={token}
          paymentId={stripe.paymentIntentId}
          onClose={() => { setEmbedOpen(false); setLoadAttempt((attempt) => attempt + 1) }}
        />
      ) : null}
      {disputeModalOpen && stripe?.paymentIntentId ? (
        <StripeDisputeDetails
          projectId={projectId}
          token={token}
          paymentId={stripe.paymentIntentId}
          onClose={() => setDisputeModalOpen(false)}
        />
      ) : null}
    </div>,
    document.body,
  )
}
